import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { fmtBytes, fmtTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { DeviceRemovalPreview } from '@/types';

type Mode = 'unpair' | 'files' | 'records';

interface UnpairResult {
  unpaired: boolean;
  queued: boolean;
  media_marked: number;
  media_kept: number;
}

/**
 * 解除配對或刪除裝置資料。裝置頁的「更多」選單與待認領清單共用。
 *
 * 先讀預覽再讓管理員選：每個做法會刪掉什麼、為什麼被擋下，都由後端
 * 算好回傳，這裡不自己推論。會刪資料的做法要輸入完整裝置編號才能送出。
 */
export function DeviceRemoval({ deviceKey, onDone }: { deviceKey: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Mode | null>(null);
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');

  const { data: p, isLoading, error } = useQuery({
    queryKey: ['device-removal', deviceKey],
    queryFn: () => apiGet<DeviceRemovalPreview>(
      `/api/v1/devices/${encodeURIComponent(deviceKey)}/removal-preview`),
    staleTime: 0,
  });

  const mut = useMutation({
    mutationFn: (mode: Mode) => apiPost<UnpairResult>(
      `/api/v1/devices/${encodeURIComponent(deviceKey)}/unpair`,
      { delete_files: mode !== 'unpair', delete_records: mode === 'records', confirm }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      void qc.invalidateQueries({ queryKey: ['devices-pending'] });
      void qc.invalidateQueries({ queryKey: ['system', 'overview'] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : '操作失敗，請重試'),
  });

  if (isLoading) {
    return <div className="grid place-items-center py-16">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>;
  }
  if (error || !p) {
    return <p className="text-[13px] text-destructive">
      載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
    </p>;
  }

  const claimed = p.claim_state === 'claimed';
  const options: { value: Mode; title: string; body: string; blockers: string[] }[] = [
    ...(claimed ? [{
      value: 'unpair' as const,
      title: '只解除配對',
      body: '事件與檔案都保留，重新認領後照常可查。',
      blockers: p.blockers.unpair,
    }] : []),
    {
      value: 'files',
      title: claimed ? '解除配對並刪除檔案' : '刪除檔案，保留事件紀錄',
      body: `刪除 ${p.media_deletable} 個檔案，事件紀錄保留。`
        + (p.media_protected ? `已列入備份或資料集的 ${p.media_protected} 個檔案會保留。` : ''),
      blockers: p.blockers.files,
    },
    {
      value: 'records',
      title: '完全移除',
      body: `刪除 ${p.events} 筆事件、${p.media_total} 個檔案、${p.commands} 筆命令與 `
        + `${p.ota_targets} 筆 OTA 紀錄，裝置從平台移除。`,
      blockers: p.blockers.records,
    },
  ];
  // 預設選第一個能執行的做法；全部被擋就停在第一個，讓原因顯示出來
  const mode = picked ?? (options.find((o) => o.blockers.length === 0) ?? options[0]).value;
  const current = options.find((o) => o.value === mode) ?? options[0];
  const deleting = mode !== 'unpair';
  const confirmed = !deleting || confirm.trim() === p.device_id;
  const busy = p.purge_state === 'queued' || p.purge_state === 'running';

  if (mut.isSuccess) {
    const r = mut.data;
    return (
      <div className="space-y-5" role="status">
        <p className="flex items-center gap-2 text-[14px] font-semibold text-success-on">
          <CheckCircle2 className="h-[18px] w-[18px]" />
          {r.queued ? '已開始刪除' : '已解除配對'}
        </p>
        <p className="text-[13px] leading-relaxed">
          {r.unpaired && '裝置已移到「待認領裝置」，平台不再接受它的資料。'}
          {r.queued && `${r.media_marked} 個檔案會在背景分批刪除，完成前待認領清單會顯示「資料刪除中」。`}
          {r.queued && mode === 'records' && '檔案刪完後，事件、命令與裝置資料會一併移除。'}
          {r.queued && r.media_kept > 0 && `另有 ${r.media_kept} 個檔案保留。`}
        </p>
        <Button onClick={onDone}>完成</Button>
      </div>
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr('');
    if (current.blockers.length || !confirmed || mut.isPending) return;
    mut.mutate(mode);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-y-2.5 text-[13px]">
        <dt className="text-muted-foreground">裝置</dt>
        <dd className="min-w-0 break-all">
          {p.label && <span className="block font-semibold">{p.label}</span>}
          <span className="font-mono text-[12px]">{p.device_id}</span>
        </dd>
        {p.unpaired_at && <>
          <dt className="text-muted-foreground">解除配對</dt>
          <dd className="font-mono text-[12px]">{fmtTime(p.unpaired_at)}</dd>
        </>}
        <dt className="text-muted-foreground">事件紀錄</dt>
        <dd><span className="tabular">{p.events}</span> 筆
          {p.reviewed_events > 0 && <span className="text-muted-foreground">（已判讀 {p.reviewed_events} 筆）</span>}
        </dd>
        <dt className="text-muted-foreground">檔案</dt>
        <dd><span className="tabular">{p.media_total}</span> 個
          <span className="text-muted-foreground">（已保存 {p.media_stored} 個，{fmtBytes(p.stored_bytes)}）</span>
        </dd>
        {p.media_protected > 0 && <>
          <dt className="text-muted-foreground">受保護檔案</dt>
          <dd>
            {p.media_protected} 個
            <span className="text-muted-foreground">
              （{[p.media_backed_up && `已備份 ${p.media_backed_up}`, p.media_in_dataset && `資料集 ${p.media_in_dataset}`]
                .filter(Boolean).join('、')}，任何做法都不會刪）
            </span>
          </dd>
        </>}
        <dt className="text-muted-foreground">命令／OTA</dt>
        <dd>{p.commands} 筆命令、{p.ota_targets} 筆 OTA 紀錄</dd>
      </dl>

      {busy && (
        <p className="flex items-center gap-2 rounded-sm border border-border bg-secondary/40 px-3.5 py-3 text-[12.5px]">
          <Loader2 className="h-[14px] w-[14px] animate-spin" />資料刪除中，完成後才能再操作。
        </p>
      )}
      {p.purge_state === 'failed' && (
        <p role="alert" className="break-words rounded-sm border border-destructive/40 bg-accent/30 px-3.5 py-3 text-[12.5px]">
          上次的刪除沒有完成：{p.purge_error || '原因不明'}。排除問題後可以再送出一次。
        </p>
      )}

      <fieldset className="space-y-2.5">
        <legend className="mb-2.5 text-[12.5px] font-semibold">要怎麼處理這台裝置</legend>
        {options.map((o) => {
          const blocked = o.blockers.length > 0;
          const active = o.value === mode;
          return (
            <label key={o.value}
                   className={`flex min-w-0 items-start gap-3 rounded-sm border p-3.5 text-[13px] transition-colors ${
                     active ? 'border-primary bg-accent/35' : 'border-border bg-card'
                   } ${blocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input type="radio" name="removal-mode" value={o.value}
                     checked={active} disabled={blocked || mut.isPending}
                     onChange={() => { setPicked(o.value); setErr(''); }}
                     className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-primary" />
              <span className="min-w-0">
                <span className={`block font-semibold ${blocked ? 'text-muted-foreground' : ''}`}>{o.title}</span>
                <span className="mt-1 block leading-relaxed text-muted-foreground">{o.body}</span>
                {o.blockers.map((b) => (
                  <span key={b} className="mt-1.5 block break-words leading-relaxed text-destructive">{b}</span>
                ))}
              </span>
            </label>
          );
        })}
      </fieldset>

      <div className="space-y-2 rounded-sm border border-warn bg-warn-soft px-3.5 py-3 text-[12.5px] leading-relaxed">
        {claimed && (
          <p>
            解除配對後，裝置會移到「待認領裝置」。平台不再接受它的事件與檔案，即時觀看會停止，還沒送出的命令會取消。裝置上的 Wi-Fi 設定與密鑰不變，之後可以直接重新認領。
          </p>
        )}
        {claimed && p.online && (
          <p>這台裝置目前在線，解除配對後仍會連上平台，只是資料不再被接受。</p>
        )}
        {mode === 'files' && (
          <p>裝置 SD 卡上的檔案不受影響。平台會留下刪除紀錄，重新認領後同一個檔案不會再上傳。</p>
        )}
        {mode === 'records' && (
          <p>
            完全移除後，這台裝置若再連上平台，會以新裝置的身分出現在待認領清單。平台不再留有刪除紀錄，重新認領後 SD 卡上的舊檔可能再上傳一次；要避免重傳，請先清空 SD 卡，或改選只刪檔案的做法。
          </p>
        )}
      </div>

      {deleting && (
        <label className="block">
          <span className="mb-2 block text-[12.5px] font-semibold">
            輸入裝置編號 <span className="select-all font-mono">{p.device_id}</span> 確認刪除
          </span>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 autoComplete="off" spellCheck={false} placeholder={p.device_id}
                 className="font-mono" disabled={mut.isPending} />
          <span className="mt-1.5 block text-[11.5px] text-muted-foreground">刪除後無法復原</span>
        </label>
      )}

      {err && <p role="alert" className="break-words text-[12.5px] font-semibold text-destructive">{err}</p>}

      <Button type="submit" variant={deleting ? 'destructive' : 'default'}
              disabled={current.blockers.length > 0 || !confirmed || mut.isPending}>
        {mut.isPending ? (
          <><Loader2 className="h-[14px] w-[14px] animate-spin" />處理中…</>
        ) : current.title}
      </Button>
    </form>
  );
}
